import { GetSignupsByShiftIdCommandOutput, Signup } from '@amzn/red-velvet-api';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { useTOptions } from '../localization/tOptions';
import { TSHIRT_SIZES } from '../../data/eventOptionLists';
import { getOptionFromValue } from '../../utils/url';
import { AmazonSignup, SignupStatus } from '../../utils/signupUtils';

const DEFAULT_PAGE_SIZE = 1000;

function useTShirtLabelMapper() {
  // tshirtSizes is a returnObjects list, which ARB bundles (flat id→string) cannot return; useTOptions
  // rebuilds it label-by-label from the code-owned value list, falling back to the legacy i18next list
  // when the Localization flag is off.
  const tShirtSizes = useTOptions(['translation'], TSHIRT_SIZES);

  return (id?: string) => {
    if (!id) return '';
    // getOptionFromValue rather than a find(): the ARB list is flat but the legacy i18next list may
    // carry option groups, and a cast to Option[] would walk straight past a grouped one.
    return getOptionFromValue(id, tShirtSizes)?.label || id;
  };
}

function toAmazonSignup(signup: Signup, getTShirtLabel: (id?: string) => string): AmazonSignup {
  const { signupUserInfo } = signup;
  return {
    signupId: signup.signupId || '',
    shiftId: signup.shiftId || '',
    name: `${signupUserInfo?.firstName || ''} ${signupUserInfo?.lastName || ''}`.trim(),
    alias: signupUserInfo?.alias || '',
    email: signupUserInfo?.primaryEmail || '',
    building: signupUserInfo?.building || '',
    managerLogin: signupUserInfo?.managerLogin || '',
    numberOfGuests: signup.guestCount ?? 0,
    tShirtSize: getTShirtLabel(signup.tShirtSizeId?.toString()),
    signupStatus: SignupStatus.SIGNUP,
    waiverAccepted: signup.waiverAccepted,
    questionAnswers: signup.questionAnswers,
  };
}

export function useAllSignupsByShiftId(shiftId: string, enabled = true) {
  const getTShirtLabel = useTShirtLabelMapper();

  const result = useAllPagesRedVelvetQuery({
    enabled: enabled && shiftId !== '',
    // Another coordinator, a volunteer self-registering, or the async auto-signup pipeline can fill
    // this shift with no mutation on this page to invalidate, which leaves the "spots available"
    // count stale. 'always' is required because the app-wide staleTime of Infinity means plain
    // `true` would never fire. This only narrows the window — it does nothing while the user stays
    // on the tab, so the server-side capacity gate remains the correctness boundary.
    refetchOnWindowFocus: 'always',
    redVelvetQueryKey: [
      'getSignupsByShiftId',
      { shiftId, includeSignupUserInfo: true, pageSize: DEFAULT_PAGE_SIZE },
    ],
    converter: (output: GetSignupsByShiftIdCommandOutput) => ({
      items: output.signups || [],
      next: output.page,
    }),
  });

  return {
    signups: (result.data || []).map((signup) => toAmazonSignup(signup, getTShirtLabel)),
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  };
}
